import { useApi } from '@/api/ApiProvider';
import { useMutation } from '@tanstack/react-query';

export interface DeepLink {
  id?: string;
  targetUrl?: string;
  deepLinkUrl?: string;
  title?: string;
  imageUrl?: string;
  description?: string;
  linkableType?: string;
  linkableId?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateDeepLinkRequest {
  targetUrl: string;
  title: string;
  imageUrl?: string;
  description?: string;
  linkableType: string;
  linkableId: string;
}

interface DeepLinkResponse {
  data: {
    id: string;
    slug?: string;
    target_url?: string;
    deep_link_url?: string;
    title?: string;
    image_url?: string;
    description?: string;
    linkable_type?: string;
    linkable_id?: string;
    user_id?: number;
    access_count?: number;
    created_at?: string;
    updated_at?: string;
    // También puede tener la estructura con attributes para compatibilidad
    attributes?: {
      target_url?: string;
      deep_link_url?: string;
      title?: string;
      image_url?: string;
      description?: string;
      linkable_type?: string;
      linkable_id?: string;
      created_at?: string;
      updated_at?: string;
    };
  };
}

export const useDeepLink = () => {
  const { mutateApi } = useApi();

  const createDeepLinkMutation = useMutation(
    (request: CreateDeepLinkRequest) =>
      mutateApi(
        ['deep_links'],
        {
          target_url: request.targetUrl,
          title: request.title,
          image_url: request.imageUrl,
          description: request.description,
          linkable_type: request.linkableType,
          linkable_id: request.linkableId,
        },
        {
          method: 'POST',
        }
      ),
    {
      onSuccess: (result) => {
        // Log removido para reducir ruido en consola
      },
      onError: (error) => {
        console.error('Error creating/retrieving deep link:', error);
      },
    }
  );

  const createDeepLink = async (request: CreateDeepLinkRequest): Promise<DeepLink | null> => {
    try {
      const result = await createDeepLinkMutation.mutateAsync(request);

      // Verificar si hay error en la respuesta
      if (result && typeof result === 'object' && 'error' in result) {
        console.error('Server returned error:', result.error);
        return null;
      }

      if (result && typeof result === 'object' && 'data' in result && result.data) {
        const deepLinkData = (result as DeepLinkResponse).data;

        // La respuesta del servidor tiene los campos directamente, no en un objeto 'attributes'
        const deepLink = {
          id: deepLinkData.id,
          targetUrl: deepLinkData.target_url || deepLinkData.attributes?.target_url,
          deepLinkUrl: deepLinkData.deep_link_url || deepLinkData.attributes?.deep_link_url,
          title: deepLinkData.title || deepLinkData.attributes?.title,
          imageUrl: deepLinkData.image_url || deepLinkData.attributes?.image_url,
          description: deepLinkData.description || deepLinkData.attributes?.description,
          linkableType: deepLinkData.linkable_type || deepLinkData.attributes?.linkable_type,
          linkableId: deepLinkData.linkable_id || deepLinkData.attributes?.linkable_id,
          createdAt: deepLinkData.created_at || deepLinkData.attributes?.created_at,
          updatedAt: deepLinkData.updated_at || deepLinkData.attributes?.updated_at,
        };

        return deepLink;
      }

      console.error('No data in result:', result);
      return null;
    } catch (error) {
      console.error('Error creating/retrieving deep link:', error);
      return null;
    }
  };

  return {
    createDeepLink,
    isLoading: createDeepLinkMutation.isLoading,
    isError: createDeepLinkMutation.isError,
    error: createDeepLinkMutation.error,
  };
};